// src/pages/VSCodePage.js
import React from 'react';
import { NavLink } from 'react-router-dom';
import PillButton from '../components/PillButton';

function VSCodePage() {
  return (
    <div>
      {/* Hero Section */}
      <header className="hero-section text-center" role="banner">
        <div className="container">
          <h1>Visual Studio Code</h1>
          <p className="lead">Master your coding environment</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5" role="main">
        <section id="vscode-content" aria-labelledby="vscode-heading">
          <h2 id="vscode-heading">Learn VS Code Basics</h2>
          <p>
            Visual Studio Code (VS Code) is a powerful, lightweight code editor perfect for frontend development. Below, explore key features, tips, and extensions to boost your productivity!
          </p>

          {/* Chunk 1: Getting Started */}
          <div className="chunk">
            <h3>1. Getting Started</h3>
            <p>Download and install VS Code to start coding efficiently.</p>
            <div className="code-snippet">
              <pre>
                {`1. Visit code.visualstudio.com
2. Download for your OS (Windows, macOS, Linux)
3. Run the installer and launch VS Code`}
              </pre>
            </div>
            <p><strong>Try it:</strong> Open VS Code and create a new file with <code>Cmd + N</code> (Mac) or <code>Ctrl + N</code> (Windows).</p>
            <div className="tip">
              <strong>Tip:</strong> Press <strong>Cmd + T</strong> (Mac) or <strong>Ctrl + T</strong> (Windows) to quickly search for files or symbols in your project.
            </div>
          </div>

          {/* Chunk 2: File Management */}
          <div className="chunk">
            <h3>2. File Management</h3>
            <p>Organize your project with VS Code’s file explorer.</p>
            <div className="code-snippet">
              <pre>
                {`- Right-click in Explorer to create new files/folders
- Drag and drop files to rearrange
- Use Cmd + S (Mac) or Ctrl + S (Windows) to save`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Press <strong>Cmd + B</strong> (Mac) or <strong>Ctrl + B</strong> (Windows) to toggle the sidebar for more workspace.
            </div>
          </div>

          {/* Chunk 3: Shortcuts */}
          <div className="chunk">
            <h3>3. Keyboard Shortcuts</h3>
            <p>Speed up your workflow with these essential shortcuts.</p>
            <div className="code-snippet">
              <pre>
                {`Cmd + / (Mac) or Ctrl + / (Windows) - Toggle comments
Cmd + D (Mac) or Ctrl + D (Windows) - Multi-cursor edit
Option + Up/Down (Mac) or Alt + Up/Down (Windows) - Move lines
Cmd + Shift + F (Mac) or Ctrl + Shift + F (Windows) - Search project`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Press <strong>Cmd + K, Cmd + S</strong> (Mac) or <strong>Ctrl + K, Ctrl + S</strong> (Windows) to view all shortcuts and customize them.
            </div>
          </div>

          {/* Chunk 4: Extensions */}
          <div className="chunk">
            <h3>4. Extensions</h3>
            <p>Enhance VS Code with powerful extensions from the marketplace.</p>
            <div className="code-snippet">
              <pre>
                {`1. Open Extensions (Cmd + Shift + X or Ctrl + Shift + X)
2. Search for an extension (e.g., "Prettier")
3. Click Install`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <strong>Cmd + Shift + P</strong> (Mac) or <strong>Ctrl + Shift + P</strong> (Windows) and type "Extensions" to manage them quickly.
            </div>
          </div>

          {/* Chunk 5: Prettier Extension */}
          <div className="chunk">
            <h3>5. Prettier - Code Formatter</h3>
            <p>Prettier keeps your code clean and consistent with automatic formatting.</p>
            <div className="code-snippet">
              <pre>
                {`1. Install "Prettier - Code formatter" extension
2. Enable "Format on Save":
   - Open Settings (Cmd + , or Ctrl + ,)
   - Search "format on save" and check it
3. Configure (optional):
   - Add to settings.json:
     "editor.defaultFormatter": "esbenp.prettier-vscode",
     "prettier.singleQuote": true`}
              </pre>
            </div>
            <p><strong>Try it:</strong> Save a file with messy code to see Prettier fix it!</p>
            <div className="tip">
              <strong>Tip:</strong> Right-click and select "Format Document" or use <strong>Shift + Option + F</strong> (Mac) or <strong>Shift + Alt + F</strong> (Windows) to format manually.
            </div>
          </div>

          {/* Chunk 6: Live Server Extension */}
          <div className="chunk">
            <h3>6. Live Server</h3>
            <p>Live Server previews your static HTML/CSS/JS files with auto-reload.</p>
            <div className="code-snippet">
              <pre>
                {`1. Install "Live Server" extension
2. Right-click an HTML file and select "Open with Live Server"
3. Edit and save - watch it reload in the browser!`}
              </pre>
            </div>
            <p><strong>Note:</strong> For React projects like this one, use <code>npm start</code> instead, as Live Server is for static files.</p>
            <div className="tip">
              <strong>Tip:</strong> Press <strong>Cmd + L, Cmd + O</strong> (Mac) or <strong>Ctrl + L, Ctrl + O</strong> (Windows) in Live Server to open in your default browser.
            </div>
          </div>

          {/* Chunk 7: Tips and Tricks */}
          <div className="chunk">
            <h3>7. Tips and Tricks</h3>
            <p>Boost your VS Code skills with these handy features.</p>
            <ul>
              <li><strong>Multi-Cursor:</strong> Hold <strong>Option</strong> (Mac) or <strong>Alt</strong> (Windows) and click to add cursors.</li>
              <li><strong>Zen Mode:</strong> Press <strong>Cmd + K, Z</strong> (Mac) or <strong>Ctrl + K, Z</strong> (Windows) for distraction-free coding.</li>
              <li><strong>Command Palette:</strong> <strong>Cmd + Shift + P</strong> (Mac) or <strong>Ctrl + Shift + P</strong> (Windows) for quick actions.</li>
              <li><strong>Git Integration:</strong> Click the source control icon to manage Git without leaving VS Code.</li>
            </ul>
            <div className="text-center mt-3">
              <NavLink to="/">
                <PillButton>Back to Home</PillButton>
              </NavLink>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Press <strong>Cmd + Shift + R</strong> (Mac) or <strong>Ctrl + Shift + R</strong> (Windows) to refactor code (e.g., rename variables) across files.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default VSCodePage;